import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SettingsIcon from '@material-ui/icons/Settings';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';

type Props = {
  onOpenUserChatRuleSettings: () => void;
};

const SidebarSetting = ({ onOpenUserChatRuleSettings }: Props): JSX.Element => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(event.target as Node)) return;
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleOpenUserChatRuleSettings = useCallback(() => {
    setOpen(false);
    onOpenUserChatRuleSettings();
  }, [onOpenUserChatRuleSettings]);

  return (
    <div ref={containerRef} style={styles.container}>
      <button type="button" style={styles.header} onClick={() => setOpen(prev => !prev)}>
        <span style={styles.headerLabel}>
          <SettingsIcon style={styles.settingsIcon} />
          <span>設定</span>
        </span>
      </button>
      {open && (
        <div style={styles.popup}>
          <button type="button" style={styles.childItem} onClick={handleOpenUserChatRuleSettings}>
            個人ルール
          </button>
        </div>
      )}
    </div>
  );
};

const buttonReset = {
  background: 'none',
  border: 'none',
  textAlign: 'left' as const,
  display: 'block' as const,
  boxSizing: 'border-box' as const,
  font: 'inherit',
  outline: 'none',
  padding: 0,
  margin: 0
};

const getStyles = (theme: AITTheme) => ({
  container: {
    marginTop: 'auto',
    borderTop: `1px solid ${theme.grayBorderColor}`,
    position: 'relative' as const
  },
  header: {
    ...buttonReset,
    width: '100%',
    padding: '14px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textColor,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  settingsIcon: {
    width: '18px',
    height: '18px'
  },
  popup: {
    position: 'absolute' as const,
    left: '12px',
    right: '12px',
    bottom: '52px',
    zIndex: 20,
    backgroundColor: theme.whiteColor,
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.14)',
    padding: '6px'
  },
  childItem: {
    ...buttonReset,
    width: '100%',
    padding: '10px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    color: theme.textColor,
    borderRadius: '6px'
  }
});

export default SidebarSetting;
